import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import {
    ControlledInput,
    ControlledInputTrigger,
    ControlledMultilineInput,
    Form,
    USE_FORM_MODE_DEFAULTS,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useFlow } from '../../common/providers/FlowProvider';
import { useFlowRouter } from '../../common/providers/FlowRouter';
import { BaseStep } from '../../common/Step';
import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
    formatNumericInputOnChange,
    formattedDollarsToCents,
} from '../../common/util/currencyUtils';
import type { AddIncomeFlowSteps } from '../AddIncomeFlow';
import IncomePayCycleSelectionSheet from '../sheets/IncomePayCycleSelectionSheet';
import { type Income, type IncomePayCycle } from '../types/Income';
import { formatPayCycle } from '../utils/incomeUtils';

const partTimeIncomeDetailsSchema = z.object({
    payPerHour: z.string().min(3), // payPerHour is the formatted string, which includes a left padding of two characters: "$ ".
    weeklyTakeHome: z.string().min(3), // weeklyTakeHome is the formatted string, which includes a left padding of two characters: "$ ".
    weeklyHours: z.string().min(1),
    payCycle: z.string().min(1, 'Required field'),
    additionalInformation: z.string().optional(),
});

type PartTimeIncomeDetailsSchema = z.infer<typeof partTimeIncomeDetailsSchema>;

export const PartTimeDetailsStep = () => {
    const { data } = useFlow<Income>();
    const { onContinue } = useFlowRouter<Income, AddIncomeFlowSteps>();

    const [isPayCycleSheetOpen, setIsPayCycleSheetOpen] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        setFocus,
        setValue,
        formState: { errors, isValid },
        trigger,
        watch,
    } = useForm<PartTimeIncomeDetailsSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(partTimeIncomeDetailsSchema),
        defaultValues: {
            payPerHour: centsToFormattedDollars(data.payPerHourCents),
            weeklyTakeHome: centsToFormattedDollars(data.weeklyTakeHomeCents),
            weeklyHours: data.weeklyHours?.toString() ?? '',
            payCycle: data.payCycle ?? '',
            additionalInformation: data.additionalInformation ?? '',
        },
    });

    const onSubmit: SubmitHandler<PartTimeIncomeDetailsSchema> = async ({
        payPerHour,
        weeklyTakeHome,
        weeklyHours,
        payCycle,
        additionalInformation,
    }: PartTimeIncomeDetailsSchema) => {
        const incomeData = {
            payPerHourCents: formattedDollarsToCents(payPerHour),
            weeklyTakeHomeCents: formattedDollarsToCents(weeklyTakeHome),
            weeklyHours: parseInt(weeklyHours, 10), // convert the weeklyHours string to number.
            payCycle: payCycle as IncomePayCycle,
            additionalInformation,
        };

        await onContinue(incomeData);
    };

    const payCycle = watch('payCycle');

    return (
        <BaseStep
            onContinue={handleSubmit(onSubmit)}
            isContinueDisabled={!isValid}
        >
            <YStack gap={12}>
                <Form onSubmit={handleSubmit(onSubmit)} marginBottom={12}>
                    <YStack gap={12}>
                        <ControlledInput
                            label="Pay per hour"
                            name="payPerHour"
                            type="numeric"
                            inputMode="numeric"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="next"
                            formatValueOnChange={formatCurrencyInputOnChange}
                            onSubmitEditing={() => {
                                setFocus('weeklyTakeHome');
                            }}
                        />
                        <ControlledInput
                            label="Weekly take-home (approximate)"
                            name="weeklyTakeHome"
                            type="numeric"
                            inputMode="numeric"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="next"
                            formatValueOnChange={formatCurrencyInputOnChange}
                            onSubmitEditing={() => {
                                setFocus('weeklyHours');
                            }}
                        />
                        <ControlledInput
                            label="Weekly hours (approximate)"
                            name="weeklyHours"
                            type="numeric"
                            inputMode="numeric"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="next"
                            formatValueOnChange={formatNumericInputOnChange}
                        />
                        <ControlledInputTrigger
                            label="Pay cycle"
                            name="payCycle"
                            formatValue={formatPayCycle}
                            control={control}
                            register={register}
                            errors={errors}
                            onTrigger={() => {
                                setIsPayCycleSheetOpen(true);
                            }}
                        />
                        <ControlledMultilineInput
                            label="Additional information (optional)"
                            name="additionalInformation"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="done"
                            blurOnSubmit={true}
                            onSubmitEditing={() => {
                                Keyboard.dismiss();
                            }}
                        />
                    </YStack>
                </Form>
            </YStack>
            <IncomePayCycleSelectionSheet
                isOpen={isPayCycleSheetOpen}
                selectedOption={payCycle as IncomePayCycle}
                onSelectOption={(payCycle: IncomePayCycle | null) => {
                    if (payCycle != null) {
                        setValue('payCycle', payCycle);
                        void trigger('payCycle'); // Manually trigger form validation after setValue
                    }
                }}
                onClose={() => {
                    setIsPayCycleSheetOpen(false);
                }}
            />
        </BaseStep>
    );
};
